import { Grid, TextField } from '@mui/material';
import { CuriosityAutocomplete, CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import Decimal from 'decimal.js';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DemandRequestWidgetConfig } from '../../DemandRequestWidget.config';
import { commonInputProps } from '../DemandRequestDetailsForm/DemandRequestDetailsForm.common';
import { I18nNamespace } from '~/common/types';
import { CurrencySelect } from '~/components/CurrencySelect/CurrencySelect';
import { useModuleConfig } from '~/components/ModuleConfigProvider';
import { WidgetField } from '~/components/WidgetField/WidgetField';
import { useFormFieldErrorTranslation } from '~/hooks';

interface DemandRequestDetailsInfoFormProps {
  referenceDisabled: boolean;
}

export const DemandRequestDetailsInfoForm = ({ referenceDisabled }: DemandRequestDetailsInfoFormProps) => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DemandRequestWidget' });
  const widgetConfig = useModuleConfig<DemandRequestWidgetConfig>();
  const { errorToHelperText } = useFormFieldErrorTranslation();
  return (
    <>
      <Grid
        container
        size={{ xs: 12 }}
        spacing={3}
        sx={{
          alignContent: 'flex-start',
        }}
      >
        <WidgetField fieldConfig={widgetConfig.externalReference}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Controller
              name="demandRequest.externalReference"
              render={({ field, fieldState: { error } }) => {
                return (
                  <TextField
                    label={t('externalReference')}
                    error={!!error}
                    helperText={errorToHelperText(error)}
                    maxRows={4}
                    {...commonInputProps}
                    {...field}
                    value={field.value || ''}
                    disabled={referenceDisabled}
                  />
                );
              }}
            />
          </Grid>
        </WidgetField>
        <WidgetField fieldConfig={widgetConfig.commodityGroup}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Controller
              name="demandRequest.commodityGroupId"
              render={({ field, fieldState: { error } }) => {
                return (
                  <CuriosityAutocomplete
                    ns={CuriosityNamespaceType.COMMODITY_GROUP}
                    fullWidth={true}
                    multiple={false}
                    simplifyValue
                    {...field}
                    TextFieldProps={{
                      error: !!error,
                      helperText: errorToHelperText(error),
                      label: t('commodityGroup'),
                    }}
                  />
                );
              }}
            />
          </Grid>
        </WidgetField>

        <Grid size={{ xs: 12, md: 8 }}>
          <Controller
            name="demandRequest.currency"
            defaultValue=""
            render={({ field, fieldState: { error } }) => (
              <CurrencySelect {...field} error={!!error} helperText={errorToHelperText(error)} {...commonInputProps} />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 8 }}>
          <Controller
            name="demandRequest.summary"
            defaultValue=""
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  label={t('description')}
                  error={!!error}
                  helperText={errorToHelperText(error)}
                  multiline
                  maxRows={4}
                  {...commonInputProps}
                  {...field}
                  value={field.value || ''}
                />
              );
            }}
          />
        </Grid>
        <WidgetField fieldConfig={widgetConfig.budget}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Controller
              name="demandRequest.budget"
              render={({ field, fieldState: { error } }) => {
                return (
                  <TextField
                    {...field}
                    value={!field.value || isNaN(field.value) ? 0 : new Decimal(field.value).toNumber()}
                    helperText={errorToHelperText(error)}
                    fullWidth
                    disabled={field.disabled}
                    onChange={(event) => {
                      const output = parseFloat(event.target.value);
                      const val = isNaN(output) ? 0 : output;
                      field.onChange(val);
                    }}
                    label={t('budget')}
                    error={!!error}
                  />
                );
              }}
            />
          </Grid>
        </WidgetField>
      </Grid>
    </>
  );
};
